import { clearDocument } from "y-indexeddb";

const ACCESS_MAP_KEY = "planvera:collab-db-access";
const LAST_RUN_KEY = "planvera:collab-cleanup-last-run";

const ONE_DAY_MS = 24 * 60 * 60 * 1000;
const TASK_DETAILS_TTL_MS = 7 * ONE_DAY_MS;
const DEFAULT_TTL_MS = 30 * ONE_DAY_MS;

export interface CollabDbCleanupAdapter {
	clearDocument(name: string): Promise<unknown>;
}

const DEFAULT_CLEANUP_ADAPTER: CollabDbCleanupAdapter = {
	clearDocument,
};

type CollabDbCategory =
	| "legacy"
	| "task-details"
	| "drawing"
	| "comments"
	| "takeoff";

/**
 * Classify a database name into a collab category.
 * Order matters — first match wins.
 */
export const classifyCollabDb = (name: string): CollabDbCategory | null => {
	if (name.endsWith(".private")) return "legacy";
	if (/^drawing\.[^.]+\.tasks$/.test(name)) return "task-details";
	if (/^drawing\.[^.]+$/.test(name)) return "drawing";
	if (name.startsWith("comments.")) return "comments";
	if (/^takeoff\.[^.]+(?:\.file\.[^.]+)?$/.test(name)) return "takeoff";
	return null;
};

const getTtlForCategory = (category: CollabDbCategory): number | null => {
	switch (category) {
		case "legacy":
			return 0; // delete unconditionally
		case "task-details":
			return TASK_DETAILS_TTL_MS;
		case "drawing":
		case "comments":
		case "takeoff":
			return DEFAULT_TTL_MS;
	}
};

const getAccessMap = (): Record<string, number> => {
	try {
		const raw = localStorage.getItem(ACCESS_MAP_KEY);
		return raw ? JSON.parse(raw) : {};
	} catch {
		return {};
	}
};

const setAccessMap = (map: Record<string, number>): void => {
	try {
		localStorage.setItem(ACCESS_MAP_KEY, JSON.stringify(map));
	} catch {
		/* ignore quota errors */
	}
};

/**
 * Record that a collab database was accessed now.
 * Call immediately after creating an IndexeddbPersistence.
 */
export const recordCollabDbAccess = (dbName: string): void => {
	try {
		const map = getAccessMap();
		map[dbName] = Date.now();
		setAccessMap(map);
	} catch {
		/* ignore */
	}
};

/**
 * Remove a database entry from the access tracking map.
 * Call when reactively cleaning up a database (auth failure).
 */
export const removeCollabDbAccessEntry = (dbName: string): void => {
	try {
		const map = getAccessMap();
		delete map[dbName];
		setAccessMap(map);
	} catch {
		/* ignore */
	}
};

/**
 * Clean up stale y-indexeddb databases on app startup.
 *
 * - Skips if offline (preserve caches for offline sessions)
 * - Runs at most once per day
 * - Treats deletion failures as expected (another tab may hold a DB open)
 */
export const cleanupStaleCollabDatabases = async (
	adapter: CollabDbCleanupAdapter = DEFAULT_CLEANUP_ADAPTER,
): Promise<void> => {
	// Skip if offline — don't delete caches during active offline sessions
	if (typeof navigator !== "undefined" && !navigator.onLine) {
		return;
	}

	// Run at most once per day
	try {
		const lastRun = localStorage.getItem(LAST_RUN_KEY);
		if (lastRun && Date.now() - Number(lastRun) < ONE_DAY_MS) {
			return;
		}
	} catch {
		/* proceed if localStorage fails */
	}

	// indexedDB.databases() may not be available in older browsers
	if (
		typeof indexedDB === "undefined" ||
		typeof indexedDB.databases !== "function"
	) {
		return;
	}

	try {
		const databases = await indexedDB.databases();
		const accessMap = getAccessMap();
		const now = Date.now();
		const deletedNames: string[] = [];

		for (const db of databases) {
			const name = db.name;
			if (!name) continue;

			const category = classifyCollabDb(name);
			if (!category) continue;

			const ttl = getTtlForCategory(category);
			if (ttl === null) continue;

			// For unconditional deletion (legacy), ttl is 0
			if (ttl === 0) {
				try {
					await adapter.clearDocument(name);
					deletedNames.push(name);
				} catch (err) {
					console.warn(
						`[collab-cleanup] Failed to delete legacy database "${name}":`,
						err,
					);
				}
				continue;
			}

			const lastAccessed = accessMap[name];

			if (lastAccessed === undefined) {
				// No access record — seed tracking with current time.
				// This preserves existing databases during first rollout
				// when no tracking data exists yet.
				accessMap[name] = now;
				continue;
			}

			if (now - lastAccessed < ttl) {
				continue; // still fresh
			}

			// Stale — delete
			try {
				await adapter.clearDocument(name);
				deletedNames.push(name);
			} catch (err) {
				console.warn(
					`[collab-cleanup] Failed to delete stale database "${name}":`,
					err,
				);
			}
		}

		// Clean up access map entries for deleted databases
		for (const name of deletedNames) {
			delete accessMap[name];
		}
		setAccessMap(accessMap);

		// Update last run timestamp
		try {
			localStorage.setItem(LAST_RUN_KEY, String(Date.now()));
		} catch {
			/* ignore */
		}

		if (deletedNames.length > 0) {
			console.log(
				`[collab-cleanup] Deleted ${deletedNames.length} stale database(s):`,
				deletedNames,
			);
		}
	} catch (err) {
		console.warn("[collab-cleanup] Startup pruning failed:", err);
	}
};
